import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { SUPPORTED_LOCALES } from '@/lib/i18n'

import { Field } from './fields'

/**
 * Les champs propres à une édition : fuseau, devise, langues.
 *
 * Partagés entre le formulaire de l'édition et celui de création : une règle
 * écrite deux fois finit par diverger, et c'est alors le serveur qui refuse,
 * avec un message que l'écran n'avait pas annoncé.
 */

// Même habillage que les champs de `fields.tsx` — recopié plutôt qu'exporté
// de là-bas, pour ne pas toucher un fichier commun à tous les écrans.
const INPUT =
  'w-full rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary ' +
  'transition duration-[var(--dur-fast)] placeholder:text-content-muted ' +
  'focus:border-champagne-400/60'

/** Les fuseaux qu'on rencontre vraiment pour une soirée LeYAMO. Pas une limite : une suggestion. */
const COMMON_TIMEZONES = [
  'Europe/Berlin',
  'Europe/Paris',
  'Europe/Brussels',
  'Europe/Zurich',
  'Europe/London',
  'Africa/Douala',
  'Africa/Lagos',
  'Africa/Abidjan',
  'America/New_York',
  'America/Montreal',
  'UTC',
]

/**
 * Le fuseau est-il connu du navigateur ?
 *
 * Indispensable avant tout appel à `toZonedInput` / `fromZonedInput` : `Intl`
 * lève une exception sur un fuseau inconnu, et une faute de frappe en cours de
 * saisie (« Europe/Ber ») ferait tomber l'écran entier.
 */
export function isValidTimeZone(timeZone: string): boolean {
  if (!timeZone) return false
  try {
    new Intl.DateTimeFormat('en', { timeZone })
    return true
  } catch {
    return false
  }
}

/** Une date lue à l'heure de l'édition, dans la langue de l'écran. */
export function formatInZone(iso: string | null | undefined, timeZone: string, locale: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone,
    }).format(date)
  } catch {
    // Fuseau inconnu du navigateur : mieux vaut une heure sans fuseau qu'un
    // écran vide.
    return date.toLocaleString(locale)
  }
}

/** Les langues dans l'ordre de la plateforme, quel que soit l'ordre des clics. */
export function orderedLocales(locales: readonly string[]): string[] {
  const known = (SUPPORTED_LOCALES as readonly string[]).filter((code) => locales.includes(code))
  const others = locales.filter((code) => !(SUPPORTED_LOCALES as readonly string[]).includes(code))
  return [...known, ...others]
}

export function TimezoneField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string
  hint?: string
  value: string
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  const listId = useId()
  const invalid = value.trim() !== '' && !isValidTimeZone(value.trim())
  return (
    <Field label={label} hint={hint}>
      <input
        value={value}
        list={listId}
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        onChange={(event) => onChange(event.target.value)}
        className={cn(INPUT, invalid && 'border-state-danger/60')}
      />
      <datalist id={listId}>
        {COMMON_TIMEZONES.map((zone) => (
          <option key={zone} value={zone} />
        ))}
      </datalist>
      {invalid ? (
        <span className="mt-1 block text-xs text-state-danger">
          {t('admin.editions.timezoneInvalid')}
        </span>
      ) : null}
    </Field>
  )
}

export function CurrencyField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string
  hint?: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <Field label={label} hint={hint}>
      <input
        value={value}
        maxLength={3}
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        // Le serveur stocke la devise en majuscules (code ISO 4217) : on la
        // montre déjà ainsi, pour que ce qu'on voit soit ce qui est enregistré.
        onChange={(event) => onChange(event.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
        className={cn(INPUT, 'uppercase tracking-wider')}
      />
    </Field>
  )
}

/**
 * Les langues de l'édition, et celle par défaut.
 *
 * Les deux vont ensemble : la langue par défaut doit toujours figurer parmi
 * les langues choisies (le serveur refuse sinon). Retirer celle qui était par
 * défaut en choisit donc une autre aussitôt, plutôt que de laisser le
 * formulaire dans un état que l'enregistrement refuserait.
 */
export function LocalesField({
  locales,
  defaultLocale,
  onChange,
  hint,
}: {
  locales: string[]
  defaultLocale: string
  onChange: (next: { locales: string[]; default_locale: string }) => void
  hint?: string
}) {
  const { t } = useTranslation()

  function toggle(code: string) {
    const has = locales.includes(code)
    // La dernière langue ne se décoche pas : une édition sans langue n'a
    // rien à montrer, et le serveur la refuserait de toute façon.
    if (has && locales.length === 1) return
    const next = orderedLocales(has ? locales.filter((value) => value !== code) : [...locales, code])
    const nextDefault = next.includes(defaultLocale) ? defaultLocale : (next[0] ?? defaultLocale)
    onChange({ locales: next, default_locale: nextDefault })
  }

  return (
    <>
      <fieldset>
        <legend className="text-caption uppercase tracking-wide text-content-muted">
          {t('admin.event.locales')}
        </legend>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {(SUPPORTED_LOCALES as readonly string[]).map((code) => {
            const checked = locales.includes(code)
            return (
              <label
                key={code}
                className={cn(
                  'flex min-h-[2.5rem] items-center gap-2 rounded-md border px-3 py-2 text-sm',
                  checked ? 'border-champagne-400/50 text-content-primary' : 'border-line-subtle text-content-muted',
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={checked && locales.length === 1}
                  onChange={() => toggle(code)}
                  className="h-4 w-4 shrink-0 accent-[var(--ember-500)]"
                />
                {t(`admin.editions.localeName.${code}`, { defaultValue: code.toUpperCase() })}
              </label>
            )
          })}
        </div>
        {hint ? <p className="mt-1.5 text-xs text-content-muted">{hint}</p> : null}
      </fieldset>

      <Field label={t('admin.event.defaultLocale')} hint={t('admin.event.defaultLocaleHint')}>
        <select
          value={defaultLocale}
          onChange={(event) => onChange({ locales, default_locale: event.target.value })}
          className={INPUT}
        >
          {locales.map((code) => (
            <option key={code} value={code}>
              {t(`admin.editions.localeName.${code}`, { defaultValue: code.toUpperCase() })}
            </option>
          ))}
        </select>
      </Field>
    </>
  )
}

/** Le pastillage d'un statut d'édition — même couleur partout où il apparaît. */
export function EditionStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation()
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide',
        status === 'published' && 'border-state-success/40 text-state-success',
        status === 'draft' && 'border-state-warning/40 text-state-warning',
        status === 'closed' && 'border-line text-content-secondary',
        status === 'archived' && 'border-line-subtle text-content-muted',
      )}
    >
      {t(`admin.editionStatus.${status}`, { defaultValue: status })}
    </span>
  )
}
